import { Session } from "../models/session/session";
import { refreshAuthentication } from "./tokenService";

type method =
| "GET"
| "POST"
| "PUT"
| "DELETE"

export interface ApiResponse<T> {
    status: number;
    statusText: string;
    data: T;
}

export interface ApiError {
    status: number;
    title: string;
}

export function isInstanceOfApiError(object: any): object is ApiError {
    return (
        object &&
        !(typeof object === "string") &&
        "status" in object &&
        typeof object.title === "string"
    );
}

// TODO data paremeter should be an options object, with body data, urlParams, etc.
function handleGetInit<TParams = unknown>(endpoint: string, data: TParams): string {
    const urlParams = new URLSearchParams();

    for (const key in data) {
        if (data.hasOwnProperty(key)) {
            const value = data[key];

            if (Array.isArray(value)) {
                value.forEach((item: string | number | Date | null) => {
                    if (item === null) {
                        urlParams.append(key, "");
                    } else {
                        const string = item?.toString();
                        if (string.length > 0) {
                            urlParams.append(key, string);
                        }
                    }
                });
            } else {
                // eslint-disable-next-line  @typescript-eslint/no-base-to-string
                const string = value?.toString();
                if (string?.length > 0) {
                    urlParams.append(key, string);
                }
            }
        }
    }

    if (!urlParams.keys().next().done) {
        endpoint += "?" + urlParams.toString();
    }

    return endpoint;
}

/**
 * Light fetch wrapper for api. Used when expecting json or text responses.
 */
export async function apiCall<TResponse = unknown, TParams = unknown>(endpoint: string, method: method, data?: TParams, signal?: AbortSignal): Promise<ApiResponse<TResponse>> {
    if (!appConfig.apiRootUrl) {
        throw new Error("appConfig.apiRootUrl not defined");
    }

    if (method === "GET" && data) {
        endpoint = handleGetInit(endpoint, data);
    }

    await refreshAuthentication();
    const apiUrl = appConfig.apiRootUrl;
    const res = await fetch(`${apiUrl}api/${endpoint}`, {
        method,
        body: (method !== "GET" && data) ? JSON.stringify(data) : null,
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": `Bearer ${await Session.getAccessToken()}`,
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Headers": "Origin, X-Requested-With, Content-Type, Accept"
        },
        signal,
    });

    const isJson = res.headers.get("content-type")?.includes("application/json");

    if (!res.ok) {
        throw isJson ? await res.json() : await res.text();
    }

    return {
        status: res.status,
        statusText: res.statusText,
        data: isJson ? await res.json() : await res.text()
    };
}

/**
 * Light fetch wrapper for api. Used to upload files.
 */
export async function apiUploadFiles<TResponse = unknown>(endpoint: string, method: method, data: FormData, signal?: AbortSignal): Promise<ApiResponse<TResponse>> {
    if (!appConfig.apiRootUrl) {
        throw new Error("appConfig.apiRootUrl not defined");
    }

    if (method === "GET" || method === "DELETE") {
        throw new Error("Method not supported with this api call");
    }

    await refreshAuthentication();
    const apiUrl = appConfig.apiRootUrl;
    const res = await fetch(`${apiUrl}api/${endpoint}`, {
        method,
        body: data,
        headers: {
            "Accept": "application/json",
            "Authorization": `Bearer ${await Session.getAccessToken()}`,
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Headers": "Origin, X-Requested-With, Content-Type, Accept"
        },
        signal,
    });

    const isJson = res.headers.get("content-type")?.includes("application/json");

    if (!res.ok) {
        throw isJson ? await res.json() : await res.text();
    }

    return {
        status: res.status,
        statusText: res.statusText,
        data: isJson ? await res.json() : await res.text()
    };
}

/**
 * Light fetch wrapper for api. Used when expecting blob from response.
 */
export async function apiCallBlob<TParams = unknown>(endpoint: string, method: method, data?: TParams, signal?: AbortSignal) {
    if (!appConfig.apiRootUrl) {
        throw new Error("appConfig.apiRootUrl not defined");
    }

    if (method === "GET" && data) {
        endpoint = handleGetInit(endpoint, data);
    }

    await refreshAuthentication();
    const apiUrl = appConfig.apiRootUrl;
    const res = await fetch(`${apiUrl}api/${endpoint}`, {
        method,
        body: (method !== "GET" && data) ? JSON.stringify(data) : null,
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": `Bearer ${await Session.getAccessToken()}`,
            "Access-Control-Allow-Origin": "*",
            "Access-Control-Allow-Headers": "Origin, X-Requested-With, Content-Type, Accept"
        },
        signal,
    });

    if (!res.ok) {
        const isJson = res.headers.get("content-type")?.includes("application/json");
        throw isJson ? await res.json() : await res.text();
    }

    return {
        status: res.status,
        statusText: res.statusText,
        data: await res.blob()
    };
}
